/**
 * Category thumbnail image URLs for the desktop `SearchSubNav` tile strip.
 *
 * Keys match `sub.id` values in `CATEGORY_TREE` (see
 * `src/config/taxonomyConfig.js`) and the `CATEGORY_COLORS` map in
 * `SearchSubNav.js`. Any tile without an entry here falls back to its
 * colour gradient — safe to leave entries blank while curating.
 *
 * ─── How to add a URL ──────────────────────────────────────────────────
 * 1. Find an image on the web (Unsplash, Pexels, the supplier's site,
 *    Pinterest, etc.).
 * 2. In your browser: RIGHT-CLICK the image → "Copy Image Address"
 *    (Chrome/Edge) or "Copy Image Link" (Safari). NOT "Copy Link" — that
 *    gives you the web page URL, not the image URL.
 * 3. The URL you want resolves to an image/* Content-Type. Typical shapes:
 *      · https://images.unsplash.com/photo-<id>?...
 *      · https://plus.unsplash.com/premium_photo-<id>?...
 *      · https://images.pexels.com/photos/<id>/...jpg
 *      · https://static.wixstatic.com/media/<file>
 *      · any CDN / S3 URL ending in .jpg / .png / .webp / .avif
 * 4. Drop it in the map below. Page URLs (e.g. `unsplash.com/photos/
 *    <slug>-<shortcode>`) WILL NOT render — Unsplash returns 401/403 for
 *    unauthenticated page fetches. Convert with step 2 first.
 *
 * ─── Size optimisation ─────────────────────────────────────────────────
 * Unsplash / plus.unsplash.com URLs support a `w=` query param. Target
 * w=400 for tile thumbnails (CSS tile is 52px × 52px, so 400px = ~8×
 * retina density; drops a ~1MB source JPEG to ~35 KB). Keep
 * `auto=format&fit=crop&q=75` for modern formats + crop-fit + sensible
 * quality. You can safely strip Unsplash's `ixid` and `ixlib` tracking
 * params — they're not needed for image delivery.
 *
 * ─── Attribution ───────────────────────────────────────────────────────
 * For Unsplash / Pexels photos, note the photographer name in a `// Photo:
 * <name>, <profile URL>` comment next to each entry. When the /credits
 * page lands later, a grep over this file collects attributions.
 */

export const CATEGORY_THUMBNAILS = {
  // Design projects tile (right-aligned in the L1 row) — local asset,
  // same grey-render family as the three L1 heroes
  designs: '/images/category-thumbnails/designs.jpg',
  // ── L1 (top-level) — hero thumbnails used by the mobile browse drawer.
  // Desktop nav uses SVG icons at L1 (not these). Transparent-background 3D
  // object renders — brick / lamp / door — so they sit on the tile surface
  // rather than on a coloured plate (hence CATEGORY_COLORS is transparent
  // for these three ids).
  materials: '/images/category-thumbnails/materials.png', // local asset
  ffe: '/images/category-thumbnails/ffe.png', // local asset
  architectural: '/images/category-thumbnails/architectural.png', // local asset

  // ── Materials › Stone ────────────────────────────────────────────────
  stone:
    'https://images.unsplash.com/photo-1554296048-b59c9fca4857?w=400&auto=format&fit=crop&q=75',
  'natural-stone':
    'https://plus.unsplash.com/premium_photo-1675288255524-272d6abe8262?w=400&auto=format&fit=crop&q=75',
  'engineered-stone':
    'https://plus.unsplash.com/premium_photo-1707074549815-ee05d9acd0f8?w=400&auto=format&fit=crop&q=75',
  'stone-specialty':
    'https://plus.unsplash.com/premium_photo-1708073341819-4edf7d77e7e4?w=400&auto=format&fit=crop&q=75',
  terrazzo: '/images/category-thumbnails/terrazzo.jpg', // local asset (Sharetribe Console lists Terrazzo under Stone)
  gemstone:
    'https://images.unsplash.com/photo-1533644312588-f060bb40797a?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Masonry & Brick ──────────────────────────────────────
  'masonry-brick':
    'https://plus.unsplash.com/premium_photo-1672816282360-bf809584bdc0?w=400&auto=format&fit=crop&q=75',
  'fired-clay-brick':
    'https://plus.unsplash.com/premium_photo-1672769397881-3321de493c40?w=400&auto=format&fit=crop&q=75',
  'brick-slip': '/images/category-thumbnails/brick-slip.jpg', // local asset
  'stone-veneer': '/images/category-thumbnails/stone-veneer.jpg', // local asset (added 2026-05-18)
  'rammed-earth': '/images/category-thumbnails/rammed-earth.jpg', // local asset
  'exposed-block': '/images/category-thumbnails/exposed-block.jpg', // local asset

  // ── Materials › Ceramic & Tile ───────────────────────────────────────
  'ceramic-tile': '/images/category-thumbnails/ceramic-tile.jpg', // local asset (refreshed May 2026)
  porcelain: '/images/category-thumbnails/porcelain.jpg', // local asset
  ceramic: '/images/category-thumbnails/ceramic.jpg', // local asset
  'peranakan-tile': '/images/category-thumbnails/peranakan-tile.jpg', // local asset (was peranakan-encaustic.jpg pre-May 2026)
  mosaic:
    'https://static.wixstatic.com/media/2b14a8_3a1d5163cd8e4a94a0baad88d7300237~mv2.png/v1/fill/w_996,h_996,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/2b14a8_3a1d5163cd8e4a94a0baad88d7300237~mv2.png',
  'terracotta-tile': '/images/category-thumbnails/terracotta-tile.jpg', // local asset
  'large-format-tile': '/images/category-thumbnails/large-format-tile.jpg', // local asset

  // ── Materials › Wood ─────────────────────────────────────────────────
  wood:
    'https://plus.unsplash.com/premium_photo-1675695700408-ca54e14503a3?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Metal ────────────────────────────────────────────────
  metal:
    'https://images.unsplash.com/photo-1657935937312-1ad849214aea?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Glass ────────────────────────────────────────────────
  glass:
    'https://images.unsplash.com/photo-1649701954723-6bdcc53aafce?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Concrete ─────────────────────────────────────────────
  concrete:
    'https://plus.unsplash.com/premium_photo-1670728879211-255c972938e8?w=400&auto=format&fit=crop&q=75',
  'epoxy-floor-coating': '/images/category-thumbnails/epoxy-floor-coating.jpg', // local asset (chip-fleck pattern, q=70)
  'cement-tile': '/images/category-thumbnails/cement-tile.jpg', // local asset (grey speckled aggregate, q=55)
  'decorative-plaster': '/images/category-thumbnails/decorative-plaster.jpg', // local asset (warm Venetian-plaster mottle)
  'art-coating': '/images/category-thumbnails/art-coating.jpg', // local asset (pale sage smooth plaster)
  // 'GFRC & Formliner' merged tile (gfrc folded into formliner) — uses the GFRC
  // panel image per request; formliner.jpg (fluted concrete) kept in-repo but unused.
  'formliner': '/images/category-thumbnails/gfrc.jpg',

  // ── Materials › Textile ─────────────────────────────────────────
  textile:
    'https://images.unsplash.com/photo-1771582970830-e5c376a4525a?w=400&auto=format&fit=crop&q=75',
  'upholstery-fabric':
    'https://images.unsplash.com/photo-1606941541145-51b0b0365115?w=400&auto=format&fit=crop&q=75',
  drapery:
    'https://plus.unsplash.com/premium_photo-1701015911683-19abc67451c5?w=400&auto=format&fit=crop&q=75',
  'wall-panel-textile':
    'https://plus.unsplash.com/premium_photo-1675738774365-c542862be2d4?w=400&auto=format&fit=crop&q=75',
  'natural-fibre':
    'https://plus.unsplash.com/premium_photo-1673637381677-19dbcedd1bd7?w=400&auto=format&fit=crop&q=75',
  'cultural-textile-malaysian':
    'https://images.unsplash.com/photo-1761515315375-1315503bb3ce?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wallcovering ─────────────────────────────────────────
  wallcovering:
    'https://images.unsplash.com/photo-1616711864346-1a94e7aad241?w=400&auto=format&fit=crop&q=75',
  'wall-vinyl':
    'https://images.unsplash.com/photo-1678916411001-52b5d79a88cd?w=400&auto=format&fit=crop&q=75',
  grasscloth: '/images/category-thumbnails/grasscloth.jpg', // local asset (woven grass texture, q=65)
  '3d-wall-panel':
    'https://plus.unsplash.com/premium_photo-1669916851191-e86e418fd368?w=400&auto=format&fit=crop&q=75',
  'architectural-film': '/images/category-thumbnails/architectural-film.jpg', // local asset (woven greyscale film texture, q=70)

  // ── Materials › Carpet & Rug ─────────────────────────────────────────
  'carpet-rug':
    'https://images.unsplash.com/photo-1542392213-7d72edb05f5c?w=400&auto=format&fit=crop&q=75',
  'broadloom-carpet': '/images/category-thumbnails/broadloom-carpet.jpg', // local asset (vertical-stripe carpet, q=55)
  'carpet-tile': '/images/category-thumbnails/carpet-tile.jpg', // local asset (vertical-stripe pile, q=55)
  'area-rug': '/images/category-thumbnails/area-rug.jpg', // local asset (contour-wave pattern, q=70)
  'entrance-mat': '/images/category-thumbnails/entrance-mat.jpg', // local asset (dark herringbone, q=55)

  // ── Materials › Resilient Flooring ───────────────────────────────────
  'resilient-flooring':
    'https://images.unsplash.com/photo-1737098137073-45954f30e290?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Leather ──────────────────────────────────────────────
  leather:
    'https://plus.unsplash.com/premium_photo-1756717227776-0bff7a6bac4b?w=400&auto=format&fit=crop&q=75',
  'genuine-leather':
    'https://plus.unsplash.com/premium_photo-1756717219075-7fad55638d71?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Paint & Finish ───────────────────────────────────────
  'paint-finish':
    'https://images.unsplash.com/photo-1612690119274-8819a81c13a2?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Laminate & Solid Surfaces ─ NEW (May 2026) ───────────
  'laminate-surfacing': '/images/category-thumbnails/laminate-surfacing.jpg', // local asset (figured wood-grain HPL)
  hpl:
    'https://images.unsplash.com/photo-1751700390161-7e9dc45ce20c?w=400&auto=format&fit=crop&q=75',
  'compact-laminate':
    'https://images.unsplash.com/photo-1763928801154-ac3227a44618?w=400&auto=format&fit=crop&q=75',
  'solid-surface':
    'https://plus.unsplash.com/premium_photo-1707074548241-701d948766e4?w=400&auto=format&fit=crop&q=75',
  'laminate-flooring':
    'https://images.unsplash.com/photo-1737098137073-45954f30e290?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Tropical & Natural ───────────────────────────────────
  'tropical-natural':
    'https://images.unsplash.com/photo-1662934164388-a7ed0505d936?w=400&auto=format&fit=crop&q=75',

  // ── FF&E › Furniture ─────────────────────────────────────────────────
  furniture:
    'https://images.unsplash.com/photo-1644022442487-5c864f9bbea6?w=400&auto=format&fit=crop&q=75',

  // ── FF&E › Tapware & Fittings ────────────────────────────────────────
  'tapware-fittings': '/images/category-thumbnails/tapware-fittings.jpg', // local asset

  // ── FF&E › Sanitaryware ──────────────────────────────────────────────
  sanitaryware: '/images/category-thumbnails/sanitaryware.jpg', // local asset

  // ── FF&E › Appliances & Kitchen ──────────────────────────────────────
  'appliances-kitchen': '/images/category-thumbnails/appliances-kitchen.jpg', // local asset

  // ── FF&E › Lighting ──────────────────────────────────────────────────
  lighting:
    'https://images.unsplash.com/photo-1620920361552-e599e85ef28f?w=400&auto=format&fit=crop&q=75',

  // ── FF&E › Window Treatments ─────────────────────────────────────────
  'window-treatments':
    'https://images.unsplash.com/photo-1662109987496-e3c211fe2d38?w=400&auto=format&fit=crop&q=75',

  // ── FF&E › Rugs & Soft Finishings ────────────────────────────────────
  'rugs-soft':
    'https://images.unsplash.com/photo-1727200453459-37b58f800aff?w=400&auto=format&fit=crop&q=75',

  // ── FF&E › Acoustical ────────────────────────────────────────────────
  acoustical:
    'https://images.unsplash.com/photo-1547921258-fbfcf52f8d5b?w=400&auto=format&fit=crop&q=75',

  // ── FF&E › Outdoor ───────────────────────────────────────────────────
  outdoor: '/images/category-thumbnails/outdoor.jpg', // local asset

  // ── FF&E › Electrical Accessories ────────────────────────────────────
  'electrical-accessories': '/images/category-thumbnails/electrical-accessories.jpg', // local asset

  // ── FF&E › Decor & Accessories ───────────────────────────────────────
  // Use `crop=center` (vs Unsplash's default `crop=entropy`) so the
  // 400×400 thumbnail is a center-cut of the source rather than a
  // high-entropy auto-crop. Keeps the subject centred in the tile.
  'decor-accessories':
    'https://images.unsplash.com/photo-1687191883740-869382375bd6?w=400&auto=format&fit=crop&crop=center&q=75',

  // ── Architectural › Ceiling Systems ──────────────────────────────────
  'ceiling-systems':
    'https://images.unsplash.com/photo-1733760746641-aebffd2a12d2?w=400&auto=format&fit=crop&q=75',

  // ── Architectural › Facade & Envelope ────────────────────────────────
  'facade-envelope':
    'https://images.unsplash.com/photo-1657577285394-4b0b4e9834a0?w=400&auto=format&fit=crop&q=75',

  // ── Architectural › Partition & Wall Systems ─────────────────────────
  'partition-wall': '/images/category-thumbnails/partition-wall.jpg', // local asset

  // ── Architectural › Doors & Openings ─────────────────────────────────
  'doors-openings': '/images/category-thumbnails/doors-openings.jpg', // local asset

  // ── Architectural › Door & Cabinet Hardware ──────────────────────────
  'door-cabinet-hardware': '/images/category-thumbnails/door-cabinet-hardware.jpg', // local asset (flipped horizontally on import)

  // ── Architectural › Paving, Decking & Landscape ──────────────────────
  'paving-decking':
    'https://images.unsplash.com/photo-1679407263245-43fd8f8f1917?w=400&auto=format&fit=crop&q=75',

  // ── Architectural › Moulding & Trim ──────────────────────────────────
  'moulding-trim':
    'https://images.unsplash.com/photo-1737294556296-62f528b88e1e?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wood › Solid Timber ──────────────────────────────────
  'solid-timber':
    'https://plus.unsplash.com/premium_photo-1672872726333-0ee6acc1fcc8?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wood › Engineered Wood ───────────────────────────────
  'engineered-wood':
    'https://plus.unsplash.com/premium_photo-1671612828903-dc019accc402?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wood › Timber Decking ────────────────────────────────
  'timber-decking':
    'https://images.unsplash.com/photo-1737094562882-ec66dbf4c3be?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wood › Veneer ────────────────────────────────────────
  veneer:
    'https://images.unsplash.com/photo-1700973408133-b45276ec8feb?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wood › Parquet ───────────────────────────────────────
  parquet: '/images/category-thumbnails/parquet.jpg', // local asset (herringbone detail, q=65)

  // ── Materials › Metal › Decorative Metal ─────────────────────────────
  'decorative-metal': '/images/category-thumbnails/decorative-metal.jpg', // local asset

  // ── Materials › Metal › Perforated / Expanded Metal ──────────────────
  // (Was `perforated-mesh` / "Perforated / Mesh" — renamed; "mesh" moved
  // to its own L3 below to host coil/woven/drapery meshes together.)
  'perforated-expanded-metal':
    'https://images.unsplash.com/photo-1774835179272-3221a4a23d84?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Metal › Metal Mesh / Drapery ─────────────────────────
  // (Was `coil-mesh` / "Coil Mesh / Drapery" — broadened to all metal
  // meshes including coil, woven, and drapery types.)
  'metal-mesh': '/images/category-thumbnails/metal-mesh.jpg', // local asset (high-freq texture, q=65)

  // ── Materials › Glass › Decorative Glass ─────────────────────────────
  // (Was `glass-panel` — renamed to Decorative Glass in taxonomy rework.)
  'decorative-glass':
    'https://images.unsplash.com/photo-1615543640706-2213bb8337b2?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Glass › Mirror ───────────────────────────────────────
  mirror: '/images/category-thumbnails/mirror.jpg', // local asset (antiqued speckle, q=60)

  // ── Materials › Glass › Exterior Glass ───────────────────────────────
  'exterior-glass': '/images/category-thumbnails/exterior-glass.jpg', // local asset

  // ── Materials › Glass › Smart / Switchable Glass ─────────────────────
  'smart-switchable-glass': '/images/category-thumbnails/smart-switchable-glass.jpg', // local asset

  // ── Materials › Concrete › Polished Concrete ─────────────────────────
  'polished-concrete':
    'https://images.unsplash.com/photo-1515895309288-a3815ab7cf81?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Concrete › Microcement ───────────────────────────────
  microcement:
    'https://images.unsplash.com/photo-1541140134513-85a161dc4a00?w=400&auto=format&fit=crop&q=75',

  // ── Materials › Wallcovering › Wallpaper ─────────────────────────────
  wallpaper: '/images/category-thumbnails/wallpaper.jpg', // local asset (dense pattern, q=55)

  // ── Materials L3s — local asset sweep (May 2026) ─────────────────────
  // Sourced from ~/Desktop/Matterax Cat Thumnails/, compressed via
  // sips -Z 600 -s formatOptions 70.
  'lvt-spc': '/images/category-thumbnails/lvt-spc.jpg',
  'vinyl-sheet': '/images/category-thumbnails/vinyl-sheet.jpg',
  'rubber-flooring': '/images/category-thumbnails/rubber-flooring.jpg',
  linoleum: '/images/category-thumbnails/linoleum.jpg',
  'faux-leather': '/images/category-thumbnails/faux-leather.jpg',
  'wall-paint': '/images/category-thumbnails/wall-paint.jpg',
  'specialty-paint': '/images/category-thumbnails/specialty-paint.jpg',
  'wood-stain-varnish': '/images/category-thumbnails/wood-stain-varnish.jpg',
  'metal-coating': '/images/category-thumbnails/metal-coating.jpg',
  'bamboo-panel': '/images/category-thumbnails/bamboo-panel.jpg',
  'rattan-panel': '/images/category-thumbnails/rattan-panel.jpg',
  cork: '/images/category-thumbnails/cork.jpg',
  sukabumi: '/images/category-thumbnails/sukabumi.jpg',
  'biophilic-panel':
    'https://plus.unsplash.com/premium_photo-1673102662773-f4d5b1ffa62a?w=400&auto=format&fit=crop&q=75',
  // (gfrc, terrazzo, brick-slip, rammed-earth, exposed-block,
  //  porcelain, ceramic, peranakan-tile, terracotta-tile,
  //  large-format-tile, parquet, decorative-metal, metal-mesh,
  //  exterior-glass, mirror, smart-switchable-glass, wallpaper,
  //  grasscloth, broadloom-carpet, carpet-tile, area-rug, entrance-mat,
  //  epoxy-floor-coating, laminate-surfacing already wired above)

  // ── Still on CDN (no source PNG curated yet) ─────────────────────────
  // L2 fallbacks: stone, masonry-brick, ceramic-tile, wood, metal,
  //   glass, concrete, textile, wallcovering, carpet-rug,
  //   resilient-flooring, leather, paint-finish, tropical-natural
  // L3s: solid-timber, engineered-wood, timber-decking, veneer,
  //   perforated-expanded-metal, decorative-glass, polished-concrete,
  //   microcement, upholstery-fabric, drapery, wall-panel-textile,
  //   natural-fibre, cultural-textile-malaysian, wall-vinyl,
  //   3d-wall-panel,
  //   fired-clay-brick, mosaic

  // ── FF&E (awaiting URLs) ─────────────────────────────────────────────
  // 'furniture':
  // 'seating':
  // 'tables':
  // 'storage-furniture':
  // 'beds':
  // 'outdoor-furniture':
  // 'sanitaryware':
  // 'wc-toilet':
  // 'basin-sink':
  // 'shower-bath':
  // 'bathroom-accessories':
  // 'tapware-fittings':
  // 'basin-tap':
  // 'shower-fitting':
  // 'kitchen-mixer':
  // 'bath-filler':
  // 'lighting':
  // 'pendant-chandelier':
  // 'recessed-downlight':
  // 'wall-sconce':
  // 'floor-table-lamp':
  // 'linear-strip':
  // 'outdoor-lighting':
  // 'appliances-kitchen':
  // 'kitchen-appliances':
  // 'kitchen-sink':
  // 'kitchen-system':
  // 'outdoor':
  // 'outdoor-seating':
  // 'outdoor-shade':
  // 'pool-equipment':
  // 'window-treatments':
  // 'curtains-drapes':
  // 'blinds-roller':
  // 'solar-shading':
  // 'rugs-soft':
  // 'decorative-rug':
  // 'cushions-throws':
  // 'bedding-linen':
  // 'acoustical':
  // 'acoustic-panel':
  // 'office-systems':              // FF&E L2 — pods, modular meeting, workstations
  // 'acoustic-pod-phone-booth':
  // 'modular-meeting-structure':
  // 'workstation-desk-system':
  // 'decor-accessories':
  // 'art-sculpture':
  // 'plants-planters':
  // 'mirrors-decorative':
  // 'vases-objects':

  // ── Architectural (awaiting URLs) ────────────────────────────────────
  // 'ceiling-systems':
  // 'metal-ceiling':
  // 'timber-ceiling':
  // 'acoustic-ceiling':
  // 'specialty-ceiling':
  // 'partition-wall':
  // 'glass-partition':
  // 'movable-partition':
  // 'toilet-cubicle-system':
  // 'facade-envelope':
  // 'cladding-panel':
  // 'curtain-wall':
  // 'screening-louvre':
  // 'insulation-membrane':
  // 'doors-openings':
  // 'timber-door':
  // 'glass-door':
  // 'fire-door':
  // 'pivot-door':
  // 'sliding-folding-door':
  // 'door-cabinet-hardware':
  // 'door-handle':
  // 'hinge':
  // 'lock-smart-lock':
  // 'cabinet-hardware':
  // 'balustrade-fitting':
  // 'door-seal':
  // 'door-threshold':
  // 'window-hardware':
  // 'escutcheon-rose-plate':
  // 'wc-privacy-fitting':
  // 'doorstop-holder':
  // 'paving-decking':
  // 'exterior-paving':
  // 'composite-decking':
  // 'permeable-paving':
  // 'pool-coping':
  // 'moulding-trim':
  // 'cornice-architrave':
  // 'skirting':
  // 'edge-trim':
  // 'shadow-gap':
};

/**
 * Ids whose thumbnail is a transparent-background ICON rather than a photo.
 * Photos are cropped to fill the tile (`cover`); icons must be fitted inside
 * it (`contain`), or the tile's non-square image area crops the render and
 * eats the padding baked into the PNG canvas.
 */
export const ICON_THUMBNAIL_IDS = new Set(['materials', 'ffe', 'architectural']);

export default CATEGORY_THUMBNAILS;
